import type { ContentChoice } from '@rollcall/contracts'
import { useT } from './i18n-context.js'

/**
 * Выборы при получении в редакторе контента (D53, D58).
 *
 * Тем же способом, что эффекты и счётчики: свой вид поля формы,
 * а не обход схемы. Обход разметил бы источник вариантов размеченным
 * объединением, и мастер выбирал бы «kind: catalogue» вместо «любой
 * навык».
 *
 * Без этого пользовательская черта «три навыка или инструмента
 * на выбор» не заводится: эффекты форма умеет, выборы — нет.
 *
 * Что спрашивается: как называется выбор, сколько брать и откуда
 * варианты. «Откуда» — либо вид записей целиком («любой навык»),
 * либо перечень ссылок через запятую. Перечень строкой, а не
 * списком полей: мастер пишет «srd:athletics, srd:stealth», и это
 * короче, чем добавлять строку на каждый вариант.
 */

/** Виды записей, из которых можно взять варианты целым каталогом. */
const CATALOGUE_TYPES = ['skill', 'language', 'feat', 'spell', 'item'] as const

type CatalogueType = (typeof CATALOGUE_TYPES)[number]

export type ChoiceEditorProps = {
  readonly choices: readonly ContentChoice[]
  readonly onChange: (next: readonly ContentChoice[]) => void
}

/** Ключ из порядкового номера: человеку он не показывается. */
function keyFor(taken: readonly ContentChoice[]): string {
  let number = taken.length + 1
  const used = new Set(taken.map((item) => item.key))
  while (used.has(`choice_${number}`)) number += 1
  return `choice_${number}`
}

/** Перечень ссылок строкой: «srd:athletics, srd:stealth». */
function optionsText(choice: ContentChoice): string {
  if (choice.from.kind !== 'list') return ''
  return choice.from.options.map((option) => option.value).join(', ')
}

function parseOptions(text: string): { value: string; label: string }[] {
  return text
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part !== '')
    // Подпись здесь — тот же идентификатор: имя придёт из самой
    // записи, когда план соберёт варианты. Выдумывать его в форме
    // значило бы завести второй источник имени.
    .map((value) => ({ value, label: value }))
}

export function ChoiceEditor({ choices, onChange }: ChoiceEditorProps) {
  const t = useT()

  const patch = (index: number, next: ContentChoice) => {
    onChange(choices.map((item, at) => (at === index ? next : item)))
  }

  return (
    <div className="app-effects">
      {choices.map((choice, index) => (
        <div key={choice.key} className="app-effect" data-choice={choice.key}>
          <label className="app-field">
            <span>{t('custom.choice.name')}</span>
            <input
              type="text"
              value={choice.name}
              onChange={(event) => {
                patch(index, { ...choice, name: event.target.value })
              }}
            />
          </label>

          <label className="app-field">
            <span>{t('custom.choice.count')}</span>
            <input
              type="number"
              inputMode="numeric"
              min={1}
              value={choice.choose}
              onChange={(event) => {
                const count = Number(event.target.value)
                patch(index, { ...choice, choose: count > 0 ? count : 1 })
              }}
            />
          </label>

          <label className="app-field">
            <span>{t('custom.choice.from')}</span>
            <select
              value={choice.from.kind === 'catalogue' ? choice.from.type : 'list'}
              onChange={(event) => {
                const picked = event.target.value
                patch(index, {
                  ...choice,
                  from:
                    picked === 'list'
                      ? { kind: 'list', options: parseOptions(optionsText(choice)) }
                      : { kind: 'catalogue', type: picked as CatalogueType },
                })
              }}
            >
              <option value="list">{t('custom.choice.list')}</option>
              {CATALOGUE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`custom.choice.type.${type}`)}
                </option>
              ))}
            </select>
          </label>

          {choice.from.kind === 'list' && (
            <label className="app-field">
              <span>{t('custom.choice.options')}</span>
              <input
                type="text"
                value={optionsText(choice)}
                placeholder="srd:athletics, srd:stealth"
                onChange={(event) => {
                  patch(index, {
                    ...choice,
                    from: { kind: 'list', options: parseOptions(event.target.value) },
                  })
                }}
              />
            </label>
          )}

          <div className="app-actions">
            <button
              type="button"
              className="app-button app-button-quiet"
              onClick={() => {
                onChange(choices.filter((_, at) => at !== index))
              }}
            >
              {t('custom.choice.remove')}
            </button>
          </div>
        </div>
      ))}

      <div className="app-actions">
        <button
          type="button"
          className="app-button"
          onClick={() => {
            onChange([
              ...choices,
              {
                key: keyFor(choices),
                name: '',
                choose: 1,
                from: { kind: 'catalogue', type: 'skill' },
              },
            ])
          }}
        >
          {t('custom.choice.add')}
        </button>
      </div>
    </div>
  )
}
